import { apiRoutes } from "@/api";
import { appConfig } from "@/config";
import { authClient } from "@/http";

/**
 * 브라우저가 없는 클라이언트(원격 셸의 Codex)를 연결하는 1회용 연결 코드다.
 *
 * Owns:
 * - 인증 서버 OTP 발급 호출과 표시 형식(`XXXXX-XXXXX`).
 * - 원격 터미널에 붙여넣는 명령 문자열 조립.
 *
 * Does not own:
 * - 코드 교환. 설치 스크립트가 인증 서버에서 코드를 직접 access token 으로 바꾼다.
 * - 설치 스크립트 본문. Registry 가 서빙한다(`apiRoutes.auth.codexInstallScript`).
 */

// OTP 를 쓸 소비처다. 인증 서버가 코드를 이 값에 연결한다. 그래서 registry 용 코드만 교환할 수 있다.
const CONNECT_CODE_CONSUMER = "registry";

// 서버가 주는 코드 길이다. 사람이 옮겨 적기 쉽도록 절반에서 나누어 표시한다.
const CODE_GROUP_LENGTH = 5;

type OtpIssueResp = {
  code: string;
  expires_at: string;
};

export type ConnectCode = {
  // 화면에 표시하고 명령에도 그대로 넣는 표시 형식이다. 교환하는 쪽이 하이픈·공백을 정규화한다.
  code: string;
  // UTC 만료 시각이다. 남은 시간을 절대 시각으로 계산해야, 모달을 열어 둔 채 시간이 지나도
  // 값이 어긋나지 않는다.
  expiresAt: string;
  // 원격 터미널에 그대로 붙여넣는 명령 한 줄이다.
  installCommand: string;
  // 실행 전에 스크립트를 읽어 보는 명령 한 줄이다. 코드를 소비하지 않는다.
  inspectCommand: string;
};

// 1회용 연결 코드를 발급받는다. 발급은 자격증명을 만드는 일이다. 그래서 응답을 로그나 에러
// 리포팅에 포함하지 않는다. 인증 서버는 Bearer 로 신원을 확인하고, registry 에 현재 멤버십을
// 다시 확인한 뒤에만 코드를 준다.
export async function issueConnectCode(accessToken: string): Promise<ConnectCode> {
  const response = await authClient.post<OtpIssueResp>(
    "/v1/auth/otp",
    { consumer: CONNECT_CODE_CONSUMER },
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );

  const code = formatConnectCode(response.data.code);
  const scriptUrl = registryOrigin() + apiRoutes.auth.codexInstallScript;
  return {
    code,
    expiresAt: response.data.expires_at,
    installCommand: `curl -fsSL ${scriptUrl} | sh -s -- ${code}`,
    inspectCommand: `curl -fsSL ${scriptUrl}`,
  };
}

// 절반에서 나누어 읽기 쉽게 만든다. 길이가 계약과 다르면 값을 바꾸지 않는다. 임의로 나누면
// 사용자가 옮겨 적는 값이 서버가 발급한 값과 달라진다.
export function formatConnectCode(rawCode: string): string {
  if (rawCode.length !== CODE_GROUP_LENGTH * 2) {
    return rawCode;
  }
  return rawCode.slice(0, CODE_GROUP_LENGTH) + "-" + rawCode.slice(CODE_GROUP_LENGTH);
}

// 명령은 다른 머신의 셸에서 실행되므로 절대 URL 이어야 한다. 배포 빌드의 `apiBaseUrl` 은
// same-origin(빈 문자열)이다. 그 경우에는 현재 페이지의 origin 이 registry origin 이다.
function registryOrigin(): string {
  return appConfig.apiBaseUrl === "" ? window.location.origin : appConfig.apiBaseUrl;
}
